/**
 * useMatch — match lifecycle hook for the Phase 2.A score-race mode.
 *
 * State machine driven by ServerMessage variants:
 *   enterQueue() → POST /enter → connect WS → join → welcome → lobby
 *     → start (phase='playing') → tick/opponent-score* → end (phase='ended').
 *
 * Phase 2.A removed garbage/KO/spectator entirely. The hook now exposes:
 *   - players: roster snapshots (from `tick` and `start`)
 *   - opponentScores: live per-userId latest score (from `opponent-score`
 *     throttled broadcasts) — the sidebar leaderboard reads this.
 *
 * The hook owns the WS lifecycle. React strict-mode double-mount safety is
 * achieved by tracking the active connection on a ref and disconnecting in
 * the effect cleanup whenever the component unmounts OR re-renders with a
 * new wsUrl.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  ClientMessage,
  MatchPhase,
  MatchPlayerState,
  Move,
  QueueEnterError,
  QueueEnterResponse,
  RoomKind,
  ServerError,
  ServerMatchEnd,
  ServerMessage,
} from '@sagwa/types'
import { PROTOCOL_VERSION, MATCH_DURATION_MS } from '@sagwa/types'
import { generateBoard } from '@sagwa/engine'
import { matchClient } from '../api/match-client'
import { MatchWsClient } from '../match/ws-client'
import { track } from '../analytics'

/**
 * Live score map keyed by userId. Updated from both `tick` snapshots
 * (authoritative full roster) and `opponent-score` deltas (throttled live
 * updates). The UI sidebar sorts by score desc.
 */
export type OpponentScore = {
  userId: number
  displayName: string
  score: number
}

export type UseMatchResult = {
  phase: MatchPhase | null
  matchId: string | null
  yourUserId: number | null
  players: MatchPlayerState[]
  yourScore: number
  remainingMs: number
  seed: number | null
  sendMove: (move: Move) => void
  /** Live per-userId latest score for the sidebar leaderboard. */
  opponentScores: OpponentScore[]
  endResult: ServerMatchEnd | null
  error: ServerError | null
  /** True between WS unexpected-close → successful retry. Drives the reconnect banner. */
  connectionLost: boolean
  /** True after the single retry fails and the parent should navigate away. */
  reconnectFailed: boolean
  /**
   * Lobby player count from `lobby` frames. Surfaced so Matchmaking.tsx can
   * show "<N> joined / <M> required" without reading the full roster.
   */
  lobbyPlayerCount: number
  /** ms remaining until forced lobby start, from the latest `lobby` frame. */
  lobbyRemainingMs: number | null
  /**
   * W2.2: room kind learned from the welcome/lobby frames. 'queue' (auto-
   * match, no Start button) or 'custom' (friend room, owner-controlled).
   * Null while the room hasn't surfaced a kind yet (default to 'queue' on
   * the UI side until first frame).
   */
  roomKind: RoomKind | null
  /** W2.2: owner userId from welcome/lobby. Null for queue rooms. */
  ownerUserId: number | null
  /** W2.2: convenience — true when current user is the room owner. */
  isOwner: boolean
  enterQueue: () => Promise<QueueEnterResponse | QueueEnterError>
  cancelQueue: () => Promise<{ ok: boolean }>
  /**
   * W2.1 hotfix: open a WS directly against a pre-issued friend-room
   * `wsUrl` (returned by POST /v1/match/room/{create,join}). Bypasses
   * `/v1/match/queue/enter` so the creator/joiner can join their own room.
   * Idempotent — repeat calls with the same wsUrl are no-ops while the
   * existing client is still connected.
   */
  connectToRoom: (opts: { wsUrl: string; matchId: string }) => void
  /**
   * W2.2: owner-only — request immediate transition from lobby to playing.
   * No-op if not currently in a custom room as the owner; server enforces
   * the gates and rejects with INVALID_MOVE otherwise.
   */
  sendOwnerStart: () => void
  /**
   * W3+W4 P1-c.2: owner-only — leave the room AND immediately transfer
   * ownership to the next-longest-joined connected non-owner player. Server
   * disconnects the original owner WS with code 1000 after broadcasting
   * `owner-changed`. No stamina refund (attempt used).
   */
  sendOwnerLeaveAndTransfer: () => void
  disconnect: () => void
  /**
   * W3+W4 P1-c: signaled when an `owner-changed` server frame arrives.
   * UI uses this to render a toast ("you are owner" vs "owner changed").
   * Reset to null when leaving the room or starting a new match.
   */
  ownerChangedAt: { newOwnerUserId: number; prevOwnerUserId: number; tsMs: number } | null
  /**
   * W3+W4 P1-d: signaled when a `room-canceled` server frame arrives.
   * RoomLobby uses this to navigate `/` with a toast keyed off the reason.
   */
  roomCanceledReason: 'owner-cancel' | 'owner-left-no-replacement' | 'ttl' | null
}

export function useMatch(): UseMatchResult {
  const [phase, setPhase] = useState<MatchPhase | null>(null)
  const [matchId, setMatchId] = useState<string | null>(null)
  const [yourUserId, setYourUserId] = useState<number | null>(null)
  const [players, setPlayers] = useState<MatchPlayerState[]>([])
  const [remainingMs, setRemainingMs] = useState<number>(MATCH_DURATION_MS)
  const [seed, setSeed] = useState<number | null>(null)
  const [opponentScoresMap, setOpponentScoresMap] = useState<
    Record<number, OpponentScore>
  >({})
  const [endResult, setEndResult] = useState<ServerMatchEnd | null>(null)
  const [error, setError] = useState<ServerError | null>(null)
  const [connectionLost, setConnectionLost] = useState(false)
  const [reconnectFailed, setReconnectFailed] = useState(false)
  const [lobbyPlayerCount, setLobbyPlayerCount] = useState(0)
  const [lobbyRemainingMs, setLobbyRemainingMs] = useState<number | null>(null)
  // W2.2: room kind + owner learned from welcome/lobby frames.
  const [roomKind, setRoomKind] = useState<RoomKind | null>(null)
  const [ownerUserId, setOwnerUserId] = useState<number | null>(null)
  // W3+W4 P1-c / P1-d: surface owner-transfer + room-canceled events so the
  // RoomLobby UI can toast / navigate. Consumers reset to null when leaving.
  const [ownerChangedAt, setOwnerChangedAt] = useState<{
    newOwnerUserId: number
    prevOwnerUserId: number
    tsMs: number
  } | null>(null)
  const [roomCanceledReason, setRoomCanceledReason] = useState<
    'owner-cancel' | 'owner-left-no-replacement' | 'ttl' | null
  >(null)

  // Refs so handler closures always see the live values.
  const clientRef = useRef<MatchWsClient | null>(null)
  const yourUserIdRef = useRef<number | null>(null)
  const mountedRef = useRef(true)
  // Track match start time so we can attach durationMs to match_end.
  const matchStartedAtRef = useRef<number | null>(null)
  // Mirror the current phase so the WS close handler can decide whether to
  // mark `connectionLost` (only meaningful while in 'lobby' or 'playing').
  const phaseRef = useRef<MatchPhase | null>(null)
  // W2 divergence diagnostic: mirror the WS-provided seed into a ref so
  // sendMove can stamp it onto outgoing move envelopes for server-side
  // forensics. Updated synchronously inside the `start` case of handleMessage.
  const seedRef = useRef<number | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      const c = clientRef.current
      if (c) {
        c.close()
        clientRef.current = null
      }
    }
  }, [])

  // Mirror phase into a ref so the WS close handler can read its current value.
  useEffect(() => {
    phaseRef.current = phase
  }, [phase])

  const handleMessage = useCallback((msg: ServerMessage): void => {
    if (!mountedRef.current) return
    switch (msg.t) {
      case 'welcome': {
        setMatchId(msg.matchId)
        setYourUserId(msg.yourUserId)
        yourUserIdRef.current = msg.yourUserId
        setPhase(msg.phase)
        // W2.2: capture roomKind + ownerUserId on first contact so the
        // Matchmaking lobby UI can branch immediately (Start vs countdown).
        if (msg.roomKind !== undefined) setRoomKind(msg.roomKind)
        if (msg.ownerUserId !== undefined) setOwnerUserId(msg.ownerUserId)
        return
      }
      case 'lobby': {
        setPhase('lobby')
        setLobbyPlayerCount(msg.playerCount)
        setLobbyRemainingMs(msg.remainingMs)
        // W2.2: server echoes roomKind + ownerUserId on every lobby frame
        // so the client can keep its branch correct across reconnects.
        if (msg.roomKind !== undefined) setRoomKind(msg.roomKind)
        if (msg.ownerUserId !== undefined) setOwnerUserId(msg.ownerUserId)
        // Phase 2.A: roster fidelity comes from `start`; here we only carry
        // the lobby count for the Matchmaking screen.
        track('match_lobby_joined', {
          playerCount: msg.playerCount,
          remainingMs: msg.remainingMs ?? 0,
        })
        return
      }
      case 'start': {
        setPhase('playing')
        setSeed(msg.seed)
        seedRef.current = msg.seed
        setPlayers(msg.players)
        setRemainingMs(MATCH_DURATION_MS)
        // Seed the opponent-score map from the roster so the sidebar
        // shows everyone at 0 immediately, before any deltas arrive.
        const initial: Record<number, OpponentScore> = {}
        for (const p of msg.players) {
          initial[p.userId] = {
            userId: p.userId,
            displayName: p.displayName,
            score: p.score,
          }
        }
        setOpponentScoresMap(initial)
        matchStartedAtRef.current = msg.startedAtMs || Date.now()
        track('match_start', {
          playerCount: msg.players.length,
          seed: msg.seed,
        })
        // W2.1 forensic: compute the bundled-engine board for the seed the
        // server just announced and ship it back as `grid-debug` so the
        // server can compare cell-by-cell against its own engine board.
        // Wrapped in try/catch so any failure in generateBoard /
        // JSON.stringify can't break the start path.
        try {
          const clientBoard = generateBoard(msg.seed)
          const gridValues: number[][] = clientBoard.map((row) =>
            row.map((cell) => (cell ? cell.value : 0)),
          )
          const c = clientRef.current
          if (c) {
            c.send({ t: 'grid-debug', gridValues, seed: msg.seed })
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[useMatch] grid-debug send failed', err)
        }
        return
      }
      case 'tick': {
        setPlayers(msg.players)
        setRemainingMs(msg.remainingMs)
        // Reconcile the opponent-score map from the authoritative roster.
        // Throttled `opponent-score` frames fill in gaps between ticks.
        setOpponentScoresMap((prev) => {
          const next: Record<number, OpponentScore> = { ...prev }
          for (const p of msg.players) {
            next[p.userId] = {
              userId: p.userId,
              displayName: p.displayName,
              score: p.score,
            }
          }
          return next
        })
        return
      }
      case 'opponent-score': {
        // Live per-player score broadcast (Phase 2.A). Update the map for
        // the sidebar leaderboard.
        setOpponentScoresMap((prev) => ({
          ...prev,
          [msg.userId]: {
            userId: msg.userId,
            displayName: msg.displayName,
            score: msg.score,
          },
        }))
        return
      }
      case 'end': {
        setPhase('ended')
        setEndResult(msg)
        const startedAt = matchStartedAtRef.current
        const durationMs = startedAt ? Math.max(0, Date.now() - startedAt) : 0
        const me = yourUserIdRef.current
        const yourRank =
          me !== null
            ? (msg.finalRanking.find((p) => p.userId === me)?.finishedRank ?? null)
            : null
        // Phase 2.A: `rank` now means score-desc rank (was elimination order
        // in Phase 2.0). PostHog event keeps the same name for continuity.
        track('match_end', {
          winnerUserId: msg.winnerUserId,
          yourRank,
          totalPlayers: msg.finalRanking.length,
          durationMs,
        })
        return
      }
      case 'error': {
        setError(msg)
        return
      }
      case 'owner-changed': {
        // W3+W4 P1-c: ownership transferred. Update the local ownerUserId so
        // isOwner derives correctly, and signal the change so the UI can toast.
        setOwnerUserId(msg.newOwnerUserId)
        setOwnerChangedAt({
          newOwnerUserId: msg.newOwnerUserId,
          prevOwnerUserId: msg.prevOwnerUserId,
          tsMs: Date.now(),
        })
        track('owner_transferred', {
          newOwnerUserId: msg.newOwnerUserId,
          prevOwnerUserId: msg.prevOwnerUserId,
        })
        return
      }
      case 'room-canceled': {
        // W3+W4 P1-d: room was cancelled. RoomLobby reads `roomCanceledReason`
        // to navigate '/' with the right toast. Phase stays as-is (server
        // will close the WS); consumers should treat any non-null reason as
        // a terminal lobby state.
        setRoomCanceledReason(msg.reason)
        track('room_canceled', { reason: msg.reason })
        return
      }
    }
  }, [])

  const handleReconnecting = useCallback((): void => {
    if (!mountedRef.current) return
    setConnectionLost(true)
    track('match_disconnect', { phase: phaseRef.current })
  }, [])

  const handleReconnected = useCallback((): void => {
    if (!mountedRef.current) return
    setConnectionLost(false)
    track('match_reconnect_success')
  }, [])

  const handleClose = useCallback((): void => {
    if (!mountedRef.current) return
    setConnectionLost(true)
    // Only flip `reconnectFailed` while there is an active match to bail out
    // of; if we've already ended or never started, leave the navigation
    // decision to the normal phase transitions.
    const p = phaseRef.current
    if (p === 'playing' || p === 'lobby') {
      setReconnectFailed(true)
      track('match_reconnect_failure', { phase: p })
    }
  }, [])

  const enterQueue = useCallback(async (): Promise<
    QueueEnterResponse | QueueEnterError
  > => {
    // W2.2 F3 (instrumentation): log when the queue (auto-match) path is
    // taken so DevTools can verify the creator/joiner is NOT silently
    // falling back to the queue when a friend-room ws should have opened.
    // eslint-disable-next-line no-console
    console.log('[useMatch] enterQueue fallback')
    setError(null)
    setConnectionLost(false)
    setReconnectFailed(false)
    setEndResult(null)
    setOpponentScoresMap({})
    setLobbyPlayerCount(0)
    setLobbyRemainingMs(null)
    setRoomKind(null)
    setOwnerUserId(null)
    setOwnerChangedAt(null)
    setRoomCanceledReason(null)
    seedRef.current = null
    const res = await matchClient.enterQueue({})
    if (!res.ok) return res
    setPhase('queued')
    setMatchId(res.matchId)
    track('match_queue_enter', { matchId: res.matchId })
    // Tear down any prior socket before starting a fresh one.
    const prior = clientRef.current
    if (prior) {
      prior.close()
      clientRef.current = null
    }
    const client = new MatchWsClient({
      wsUrl: res.wsUrl,
      onMessage: handleMessage,
      onClose: handleClose,
      onReconnecting: handleReconnecting,
      onReconnected: handleReconnected,
    })
    clientRef.current = client
    // Send the join handshake. The client buffers nothing — the WS may not
    // be OPEN yet, so wait one tick before sending. We retry-via-setTimeout
    // up to ~1s; the welcome message arrives only after the server has
    // accepted the join.
    const join: ClientMessage = { t: 'join', protocolVersion: PROTOCOL_VERSION }
    const tryJoin = (attempt: number): void => {
      if (!clientRef.current) return
      const ws = (clientRef.current as unknown as { socket: WebSocket | null })
        .socket
      if (ws && ws.readyState === WebSocket.OPEN) {
        clientRef.current.send(join)
        return
      }
      if (attempt >= 20) return
      window.setTimeout(() => tryJoin(attempt + 1), 50)
    }
    tryJoin(0)
    return res
  }, [handleMessage, handleClose, handleReconnecting, handleReconnected])

  // W2.1 hotfix: friend-room handshake bypass. createRoom / joinRoom returned
  // a wsUrl + matchId — open the WS directly without re-issuing /queue/enter.
  // Mirrors the tail of enterQueue() from setPhase('queued') onwards, minus
  // the REST call and the success/failure branching.
  const connectToRoom = useCallback(
    (opts: { wsUrl: string; matchId: string }): void => {
      // W2.2 F3 (instrumentation): log when the friend-room ws path is
      // taken so DevTools can confirm B1+B2 fix is live (creator's ws
      // actually opens after navigating to /matchmaking).
      // eslint-disable-next-line no-console
      console.log('[useMatch] connectToRoom firing', {
        wsUrl: opts.wsUrl,
        matchId: opts.matchId,
      })
      // Idempotency: if we're already attached to a client AND already past
      // welcome (matchId set), skip. This guards against effect double-fire
      // under React 18 strict mode.
      if (clientRef.current && matchId === opts.matchId) return
      setError(null)
      setConnectionLost(false)
      setReconnectFailed(false)
      setEndResult(null)
      setOpponentScoresMap({})
      setLobbyPlayerCount(0)
      setLobbyRemainingMs(null)
      setRoomKind(null)
      setOwnerUserId(null)
      setOwnerChangedAt(null)
      setRoomCanceledReason(null)
      seedRef.current = null
      setPhase('queued')
      setMatchId(opts.matchId)
      track('match_queue_enter', { matchId: opts.matchId })
      const prior = clientRef.current
      if (prior) {
        prior.close()
        clientRef.current = null
      }
      const client = new MatchWsClient({
        wsUrl: opts.wsUrl,
        onMessage: handleMessage,
        onClose: handleClose,
        onReconnecting: handleReconnecting,
        onReconnected: handleReconnected,
      })
      clientRef.current = client
      const join: ClientMessage = { t: 'join', protocolVersion: PROTOCOL_VERSION }
      const tryJoin = (attempt: number): void => {
        if (!clientRef.current) return
        const ws = (clientRef.current as unknown as { socket: WebSocket | null })
          .socket
        if (ws && ws.readyState === WebSocket.OPEN) {
          clientRef.current.send(join)
          return
        }
        if (attempt >= 20) return
        window.setTimeout(() => tryJoin(attempt + 1), 50)
      }
      tryJoin(0)
    },
    [handleMessage, handleClose, handleReconnecting, handleReconnected, matchId],
  )

  const cancelQueue = useCallback(async (): Promise<{ ok: boolean }> => {
    track('match_queue_cancel')
    const res = await matchClient.cancelQueue()
    const c = clientRef.current
    if (c) {
      c.send({ t: 'leave' })
      c.close()
      clientRef.current = null
    }
    setPhase(null)
    setMatchId(null)
    setPlayers([])
    setLobbyPlayerCount(0)
    setLobbyRemainingMs(null)
    return res
  }, [])

  const sendMove = useCallback((move: Move): void => {
    const c = clientRef.current
    if (!c) return
    // W2 divergence diagnostic (ralplan commit 1): stamp the client-side
    // seed onto every outgoing move so the server can log it alongside
    // `this.seed` for forensic comparison. The field is optional in the
    // ClientMoveSchema during the W2 transition window.
    const clientSeed = seedRef.current
    const frame: ClientMessage =
      clientSeed !== null
        ? { t: 'move', move, clientSeed }
        : { t: 'move', move }
    c.send(frame)
  }, [])

  const disconnect = useCallback((): void => {
    const c = clientRef.current
    if (c) {
      c.close()
      clientRef.current = null
    }
    setPhase(null)
    setMatchId(null)
    setPlayers([])
    setEndResult(null)
    setError(null)
    setConnectionLost(false)
    setReconnectFailed(false)
    setLobbyPlayerCount(0)
    setLobbyRemainingMs(null)
    setRoomKind(null)
    setOwnerUserId(null)
    setOwnerChangedAt(null)
    setRoomCanceledReason(null)
  }, [])

  /**
   * W2.2: owner-only — fire the 'owner-start' WS frame. No client-side
   * gating beyond "we have a socket"; the server enforces roomKind /
   * ownerId / phase / MIN_MATCH_PLAYERS and emits INVALID_MOVE on refusal.
   */
  const sendOwnerStart = useCallback((): void => {
    const c = clientRef.current
    if (!c) return
    c.send({ t: 'owner-start' })
  }, [])

  /**
   * W3+W4 P1-c.2: owner-only — leave the room and transfer ownership to the
   * longest-joined connected non-owner player. No client-side gating beyond
   * "we have a socket"; the server enforces roomKind / ownerId / phase and
   * emits INVALID_MOVE on refusal.
   */
  const sendOwnerLeaveAndTransfer = useCallback((): void => {
    const c = clientRef.current
    if (!c) return
    c.send({ t: 'owner-leave-and-transfer' })
  }, [])

  // Derive yourScore from the players array.
  const me = players.find((p) => p.userId === yourUserId) ?? null
  const yourScore = me?.score ?? 0

  // Materialize opponent-score map → sorted array, score desc with stable
  // tiebreak on userId. Recomputed only when the map identity changes.
  const opponentScores: OpponentScore[] = Object.values(opponentScoresMap).sort(
    (a, b) => {
      if (b.score !== a.score) return b.score - a.score
      return a.userId - b.userId
    },
  )

  // W2.2: derive isOwner from the welcome/lobby-provided ownerUserId.
  const isOwner =
    yourUserId !== null && ownerUserId !== null && yourUserId === ownerUserId

  return {
    phase,
    matchId,
    yourUserId,
    players,
    yourScore,
    remainingMs,
    seed,
    sendMove,
    opponentScores,
    endResult,
    error,
    connectionLost,
    reconnectFailed,
    lobbyPlayerCount,
    lobbyRemainingMs,
    roomKind,
    ownerUserId,
    isOwner,
    enterQueue,
    cancelQueue,
    connectToRoom,
    sendOwnerStart,
    sendOwnerLeaveAndTransfer,
    disconnect,
    ownerChangedAt,
    roomCanceledReason,
  }
}
